import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";

import "#styles.css";
import "#staff-operations.css";
import { AppShell } from "#app-shell.tsx";
import { AuthProvider } from "#auth.tsx";
import { LiveOrdersPage } from "#live-orders-page.tsx";
import { LoginPage } from "#login-page.tsx";
import { PreordersPage } from "#preorders-page.tsx";
import { ItemAvailabilityPage } from "#item-availability-page.tsx";
import { ProtectedRoute, RoleRoute } from "#protected-route.tsx";
import { StaffApi, clientConfig } from "#api.ts";
import { OrdersProvider } from "#orders-context.tsx";
import { SettingsPage } from "#settings-page.tsx";
import { DashboardPage } from "#dashboard-page.tsx";
import { CustomerDisplay } from "#customer-display.tsx";
import { OrderTracking } from "#order-tracking.tsx";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LanguageProvider } from "#i18n.tsx";

const root = document.getElementById("root");

if (!root) throw new Error("Missing application root.");

const config = clientConfig(import.meta.env.VITE_STAGE);
const queryClient = new QueryClient();

if (!config.ok) {
  root.textContent = config.error.message;
} else {
  // Tracking links may contain customer access tokens. Do not initialise tracing
  // on that public surface, and never record staff request headers or payloads.
  if (
    import.meta.env.VITE_SENTRY_DSN &&
    !window.location.pathname.startsWith("/orders/")
  ) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_STAGE,
      sendDefaultPii: false,
      beforeBreadcrumb: () => null,
      beforeSend: (event) => {
        delete event.request;
        delete event.user;
        delete event.extra;

        return event;
      },
    });
  }

  const api = new StaffApi(config.value);
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider api={api}>
            <LanguageProvider>
              <Sentry.ErrorBoundary
                fallback={
                  <main className="tracking-page">
                    <h1>Landpad could not display this page</h1>
                    <p>Refresh to reconnect to your orders.</p>
                    <button
                      className="primary"
                      onClick={() => window.location.reload()}
                    >
                      Reload Landpad
                    </button>
                  </main>
                }
              >
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/orders/:orderId" element={<OrderTracking />} />
                  <Route element={<ProtectedRoute />}>
                    <Route
                      element={
                        <OrdersProvider>
                          <AppShell />
                        </OrdersProvider>
                      }
                    >
                      <Route
                        path="/admin/kitchen"
                        element={<LiveOrdersPage />}
                      />
                      <Route
                        path="/admin/menu"
                        element={<ItemAvailabilityPage />}
                      />
                      <Route element={<RoleRoute allow={["admin"]} />}>
                        <Route
                          path="/admin/preorders"
                          element={<PreordersPage />}
                        />
                        <Route
                          path="/admin/pos"
                          element={<LiveOrdersPage layout="table" />}
                        />
                        <Route
                          path="/admin/dashboard"
                          element={<DashboardPage />}
                        />
                        <Route path="/orders" element={<CustomerDisplay />} />
                        <Route path="/settings" element={<SettingsPage />} />
                      </Route>
                      <Route
                        path="*"
                        element={<Navigate to="/admin/kitchen" replace />}
                      />
                    </Route>
                  </Route>
                </Routes>
              </Sentry.ErrorBoundary>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}
